<script setup lang="ts">
/**
 * Тост с отменой (DEV-2052): «DEV-X закрыта · Отменить». Свой компонент, а не
 * useToast дизайн-системы: у того нет кнопки действия, а закрытие выбрасывает
 * задачу из планов — молчать об этом нельзя, и отмена должна быть в один клик.
 */
import { onUnmounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    message: string
    actionLabel: string
    /** Второе действие: копирование. Кнопки нет, пока подпись не передали. */
    copyLabel?: string
    duration?: number
  }>(),
  { copyLabel: '', duration: 6000 },
)

const emit = defineEmits<{ action: []; copy: [] }>()

const isOpen = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined

const show = () => {
  isOpen.value = true

  if (timer) clearTimeout(timer)

  timer = setTimeout(() => {
    isOpen.value = false
  }, props.duration)
}

const act = () => {
  isOpen.value = false
  emit('action')
}

// Новый текст перезапускает таймер: два закрытия подряд — два шанса отменить.
watch(
  () => props.message,
  (message) => {
    if (message) show()
  },
)

onUnmounted(() => {
  if (timer) clearTimeout(timer)
})

defineExpose({ show })
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen" class="undo" role="status">
      <span class="undo__text">{{ message }}</span>
      <!-- Копирование не закрывает плашку: ссылку берут и тут же идут по ней. -->
      <button v-if="copyLabel" type="button" class="undo__action" @click="emit('copy')">
        {{ copyLabel }}
      </button>
      <button type="button" class="undo__action" @click="act">{{ actionLabel }}</button>
    </div>
  </Teleport>
</template>

<style scoped>
.undo {
  position: fixed;
  bottom: calc(20px + env(safe-area-inset-bottom));
  left: 50%;
  z-index: 950;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: var(--radius-lg);
  background: var(--color-text-primary);
  color: var(--color-surface);
  box-shadow: var(--shadow-lg);
  font-size: var(--font-size-body-sm);
  transform: translateX(-50%);
  /* Без max-content ширину режет left: 50% — на телефоне текст рвался по слогам. */
  width: max-content;
  max-width: calc(100vw - 32px);
}

.undo__action {
  white-space: nowrap;
  border: none;
  background: none;
  color: inherit;
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  text-decoration: underline;
  cursor: pointer;
}
</style>
